// SPDX-License-Identifier: AGPL-3.0-or-later

import {NearViewportSurfaceContext, resolveObserverRoot} from '@app/features/messaging/hooks/useNearViewport';
import {getFullscreenElement} from '@app/features/platform/utils/FullscreenMediaUtils';
import {observeIntersection} from '@app/features/platform/utils/SharedIntersectionObserver';
import {
	cancelOffscreenRelease,
	scheduleOffscreenRelease,
} from '@app/features/voice/components/media_player/utils/VideoPlaybackRegistry';
import {useContext, useEffect, useRef} from 'react';

interface UseOffscreenReleaseOptions {
	containerRef: React.RefObject<Element | null>;
	mediaRef: React.RefObject<HTMLMediaElement | null>;
	enabled: boolean;
	pause: () => void;
	release: () => void;
}

export function useOffscreenRelease({
	containerRef,
	mediaRef,
	enabled,
	pause,
	release,
}: UseOffscreenReleaseOptions): void {
	const resolveScrollSurface = useContext(NearViewportSurfaceContext);
	const callbacksRef = useRef({pause, release});
	callbacksRef.current = {pause, release};
	useEffect(() => {
		const container = containerRef.current;
		const media = mediaRef.current;
		if (!enabled || !container || !media || typeof IntersectionObserver === 'undefined') return;
		let offscreen = false;
		const shownOutsidePage = () =>
			getFullscreenElement()?.contains(media) === true || document.pictureInPictureElement === media;
		const releaseIfPaused = () => {
			if (media.paused && !shownOutsidePage()) callbacksRef.current.release();
		};
		const handlePlay = () => cancelOffscreenRelease(media);
		const handlePause = () => {
			if (offscreen) scheduleOffscreenRelease(media, releaseIfPaused);
		};
		const stopObserving = observeIntersection(
			container,
			(entry) => {
				offscreen = !entry.isIntersecting;
				if (!offscreen) {
					cancelOffscreenRelease(media);
					return;
				}
				if (shownOutsidePage()) return;
				if (!media.paused) callbacksRef.current.pause();
				scheduleOffscreenRelease(media, releaseIfPaused);
			},
			{root: resolveObserverRoot(resolveScrollSurface, container)},
		);
		media.addEventListener('play', handlePlay);
		media.addEventListener('pause', handlePause);
		return () => {
			stopObserving();
			media.removeEventListener('play', handlePlay);
			media.removeEventListener('pause', handlePause);
			cancelOffscreenRelease(media);
		};
	}, [enabled, containerRef, mediaRef, resolveScrollSurface]);
}
